import { useState } from 'react'
import { Check, ExternalLink, Loader2, RefreshCw, ShieldAlert, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import { useCan } from '@/shared/auth/session'
import { ErrorStrip } from '@/shared/ui/states'
import {
  applyProposal, decideProposal, refreshDryRun, type Proposal,
} from '../api'
import { PROPOSAL_KIND_LABEL, PROPOSAL_STATUS_LABEL } from '../vocab'

/**
 * One proposal, and the two gates it has to pass.
 *
 * ## Why the Apply button is often greyed out, and says why
 *
 * Applying needs `advisor:apply` AND the proposal&rsquo;s own authority —
 * `program:update` to move a budget, `rbac:assign` to change a role. The server
 * answers that second check in advance as `callerMayApply`, so this card can
 * name the missing authority instead of letting somebody find out by pressing
 * the button.
 *
 * ## Why Apply usually does not change anything
 *
 * With one exception — a content brief, whose whole content is a request to
 * generate media inside this module — applying records the decision and hands off
 * to the screen that owns the change. That is a deliberate limit: an advisory
 * console that could move a budget or revoke a role because a model recommended
 * it and somebody clicked twice is a privilege-escalation path with a friendly
 * name.
 *
 * What makes the hand-off worth doing is the DRY RUN, computed against the live
 * database before anybody decides — “this touches 214 beneficiaries, 31 of them
 * mid-cycle”, not “the AI suggests moving KES 400,000”.
 */

export function ProposalCard({
  proposal: initial,
  onChanged,
}: {
  proposal: Proposal
  onChanged?: (proposal: Proposal) => void
}) {
  const [proposal, setProposal] = useState(initial)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [rejecting, setRejecting] = useState(false)
  const mayApprove = useCan('advisor:approve')

  const update = (next: Proposal) => {
    setProposal(next)
    onChanged?.(next)
  }

  const run = async (what: string, fn: () => Promise<Proposal>) => {
    setBusy(what)
    setError(null)
    try {
      update(await fn())
      setRejecting(false)
      setNote('')
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const pending = proposal.status === 'PROPOSED'
  const approved = proposal.status === 'APPROVED'

  return (
    <li className="rounded-md border border-border">
      <div className="px-3 py-2.5 space-y-1.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded border border-border px-1.5 py-px text-erp-xs
                text-muted-foreground">
                {PROPOSAL_KIND_LABEL[proposal.kind] ?? proposal.kind}
              </span>
              <span className={cn('text-erp-xs',
                proposal.status === 'APPLIED' && 'text-emerald-700 dark:text-emerald-400',
                proposal.status === 'REJECTED' && 'text-muted-foreground',
                proposal.status === 'FAILED' && 'text-red-700 dark:text-red-400',
                proposal.status === 'EXPIRED' && 'text-muted-foreground/70',
              )}>
                {PROPOSAL_STATUS_LABEL[proposal.status] ?? proposal.status}
              </span>
            </div>
            <div className="text-erp-sm font-medium mt-1">{proposal.title}</div>
          </div>
        </div>

        <p className="text-erp-sm text-muted-foreground">{proposal.rationale}</p>

        {proposal.expectedEffect && (
          <p className="text-erp-xs text-muted-foreground">
            <span className="font-medium">If done: </span>{proposal.expectedEffect}
          </p>
        )}
        {proposal.riskNote && (
          <p className="text-erp-xs text-amber-800 dark:text-amber-400">
            <span className="font-medium">Cost or risk: </span>{proposal.riskNote}
          </p>
        )}

        <DryRun proposal={proposal} />

        <ErrorStrip message={error} className="rounded-md border" />

        {proposal.decisionNote && (
          <p className="text-erp-xs text-muted-foreground">
            <span className="font-medium">Decision note: </span>{proposal.decisionNote}
            {proposal.decidedAt && ` · ${fmtDateTime(proposal.decidedAt)}`}
          </p>
        )}

        {proposal.applyResult && (
          <div className="rounded-md border border-border bg-muted/30 px-2.5 py-2 text-erp-xs">
            <div className="font-medium">Result</div>
            <pre className="mt-0.5 whitespace-pre-wrap text-muted-foreground">
              {JSON.stringify(proposal.applyResult, null, 2)}
            </pre>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          {pending && mayApprove && !rejecting && (
            <>
              <button type="button" disabled={busy !== null}
                onClick={() => void run('approve', () => decideProposal(proposal.id, true))}
                className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5">
                {busy === 'approve'
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Check className="h-3.5 w-3.5" />}
                Approve
              </button>
              <button type="button" disabled={busy !== null}
                onClick={() => setRejecting(true)}
                className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5">
                <X className="h-3.5 w-3.5" /> Reject
              </button>
            </>
          )}

          {rejecting && (
            <div className="w-full space-y-1.5">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Why is this being rejected? Required — and it is the most useful
                  signal this module ever gets about its own prompts."
                className="erp-input w-full text-erp-sm"
              />
              <div className="flex gap-2">
                <button type="button" disabled={busy !== null || note.trim().length === 0}
                  onClick={() => void run('reject',
                    () => decideProposal(proposal.id, false, note.trim()))}
                  className="erp-btn h-8 px-2.5 text-erp-xs">
                  Confirm rejection
                </button>
                <button type="button" onClick={() => { setRejecting(false); setNote('') }}
                  className="erp-btn h-8 px-2.5 text-erp-xs">Cancel</button>
              </div>
            </div>
          )}

          {approved && (
            proposal.callerMayApply ? (
              <button type="button" disabled={busy !== null}
                onClick={() => void run('apply', () => applyProposal(proposal.id))}
                className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5">
                {busy === 'apply'
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Check className="h-3.5 w-3.5" />}
                Apply
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-erp-xs text-muted-foreground">
                <ShieldAlert className="h-3.5 w-3.5" />
                Applying this needs <code className="rounded bg-muted px-1">
                  {proposal.requiredAuthority}</code> as well as advisor:apply
              </span>
            )
          )}

          {pending && (
            <button type="button" disabled={busy !== null}
              onClick={() => void run('dry', () => refreshDryRun(proposal.id))}
              className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5">
              {busy === 'dry'
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5" />}
              Re-check against today&rsquo;s data
            </button>
          )}

          {proposal.deepLink && (
            <a href={proposal.deepLink}
              className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5">
              <ExternalLink className="h-3.5 w-3.5" /> Open the owning screen
            </a>
          )}
        </div>
      </div>
    </li>
  )
}

/** The preview, computed by the platform against the live database. */
function DryRun({ proposal }: { proposal: Proposal }) {
  const dry = proposal.dryRun
  if (!dry) {
    return (
      <p className="text-erp-xs text-muted-foreground/70">
        No preview has been computed yet.
      </p>
    )
  }
  const entries = Object.entries(dry).filter(
    ([key]) => !['kind', 'requiredAuthority', 'deepLink', 'appliesInModule'].includes(key))
  if (entries.length === 0) return null

  return (
    <div className="rounded-md border border-border bg-muted/20 px-2.5 py-2">
      <div className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground">
        What this actually touches
      </div>
      <dl className="mt-1 space-y-0.5">
        {entries.map(([key, value]) => (
          <div key={key} className="text-erp-xs">
            <dt className="inline text-muted-foreground">{key}: </dt>
            <dd className="inline">
              {typeof value === 'object' && value !== null
                ? JSON.stringify(value)
                : String(value)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
